import { type ReactNode, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { AuthError } from 'aws-amplify/auth';
import QRCodeSVG from 'qrcode.react';
import { z } from 'zod';
import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod';

import { Button } from '@shieldpay/bumblebee/button';
import { Form } from '@shieldpay/bumblebee/form';
import { Heading } from '@shieldpay/bumblebee/heading';
import { InputField } from '@shieldpay/bumblebee/input-field';
import { Link } from '@shieldpay/bumblebee/link';
import { Text } from '@shieldpay/bumblebee/text';
import { TextList } from '@shieldpay/bumblebee/text-list';

const FieldSchema = z.object({
  mfaCode: z.string({
    required_error: 'login.validation.password.mfaSetupCode',
  }),
});

export type MfaSetupFormFields = z.infer<typeof FieldSchema>;

export type MfaSetupFormProps = {
  username: string;
  totpCode: string;
  errorState: AuthError['name'] | undefined;
  resetErrorState: () => void;
  submitting?: boolean;
  issuer: string;
  onSubmit: (data: MfaSetupFormFields) => void;
};

export const TEST_IDS = {
  QR_CODE: 'mfa-setup--qr-code',
};

export const MfaSetupForm = ({
  onSubmit,
  totpCode,
  errorState,
  resetErrorState,
  issuer,
  username,
  submitting,
}: MfaSetupFormProps) => {
  const { formatMessage } = useIntl();

  const qrCodeStr = `otpauth://totp/AWSCognito:${username}?secret=${totpCode}&issuer=${issuer}`;

  const [submittedFormData, setSubmittedFormData] = useState<FormData | null>();

  // lastResult should usually come from the server, but we need to handle it differently
  // here because Amplify Auth runs client-side
  const lastResult = useMemo(
    () =>
      submittedFormData
        ? parseWithZod(submittedFormData, {
            schema: FieldSchema.transform((data, ctx) => {
              if (
                errorState === 'EnableSoftwareTokenMFAException' ||
                errorState === 'InvalidParameterException'
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'login.validation.password.mfaSetupCode',
                  path: ['mfaCode'],
                });
                return z.NEVER;
              }

              return data;
            }),
          }).reply()
        : null,

    [errorState, submittedFormData],
  );

  const [form, fields] = useForm({
    lastResult,
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    onValidate: ({ formData }) => {
      if (errorState) resetErrorState();

      return parseWithZod(formData, { schema: FieldSchema });
    },

    onSubmit: (e, { formData, submission }) => {
      // Prevent the default form submission - auth is handled client-side
      e.preventDefault();

      // store the last submitted result so we can rerun validation with the external error state
      setSubmittedFormData(formData);

      onSubmit(FieldSchema.parse(submission?.payload));
    },
  });

  return (
    <Form
      {...getFormProps(form)}
      spacing="basePos3"
      title={formatMessage({ id: 'login.connectAuth.heading' })}
      autoComplete="off"
    >
      <Heading>
        <FormattedMessage id="login.connectAuth.heading" />
      </Heading>
      <input type="hidden" name="challenge" value="MFA_SETUP" />
      <Text paragraph>
        <FormattedMessage
          id="login.connectAuth.description"
          values={{
            AndroidLink: (text: ReactNode) => (
              <Link href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2">
                {text}
              </Link>
            ),
            IOSLink: (text: ReactNode) => (
              <Link href="https://apps.apple.com/gb/app/google-authenticator/id388497605">
                {text}
              </Link>
            ),
          }}
        />
      </Text>
      <QRCodeSVG
        value={qrCodeStr}
        data-testid={TEST_IDS.QR_CODE}
        data-qrcode={totpCode}
      />
      <TextList ordered>
        <TextList.Item key="connect-auth-instructions-1">
          <FormattedMessage id="login.connectAuth.instructions1" />
        </TextList.Item>
        <TextList.Item key="connect-auth-instructions-2">
          <FormattedMessage id="login.connectAuth.instructions2" />
        </TextList.Item>
      </TextList>

      <InputField
        label={<FormattedMessage id="shared.enterCode" />}
        {...getInputProps(fields.mfaCode, { type: 'text' })}
        errors={fields.mfaCode.errors?.map((id) => formatMessage({ id }))}
      />
      <Button type="submit" size="large" loading={submitting}>
        <FormattedMessage id="shared.continue" />
      </Button>
    </Form>
  );
};
